import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useMemo, useRef} from 'react';

import {WarehouseModelData} from 'insights/warehouse/warehouseModelsDataLayout';
import {GRAY_LIGHTER} from 'ui-library/Colors';
import useDisabledHorizontalOverscroll from 'ui-library/hooks/useDisabledHorizontalOverscroll';
import useDocumentCursor from 'ui-library/hooks/useDocumentCursor';
import useOnMount from 'ui-library/hooks/useOnMount';
import throttle from 'util/throttle';

import useDiagramLayoutState from './diagram-layout/useDiagramLayout';
import DiagramLayoutContext, {useDiagramLayoutContext} from './DiagramLayoutContext';
import DiagramPanningContext from './DiagramPanningContext';
import useDiagramPanningState from './useDiagramPanningState';
import WarehouseDiagramLegend from './WarehouseDiagramLegend';
import WarehouseDiagramRelationshipSVG from './WarehouseDiagramRelationshipSVG';
import WarehouseDiagramTable from './WarehouseDiagramTable';
import WarehouseRelationshipTooltip from './WarehouseRelationshipTooltip';

export interface WarehouseDiagramProps {
  schemaName: string;
  focusTableName: string;
  warehouseModels: WarehouseModelData;
}

function WarehouseDiagramContents({
  schemaName,
  focusTableName,
  warehouseModels,
}: WarehouseDiagramProps): JSX.Element {
  const {layout, cellIsObscured, hideRelationshipTooltip, stopHoverRelationship} = useDiagramLayoutContext();
  useDisabledHorizontalOverscroll();

  // You can pan the warehouse diagram by either click-and-dragging around, or by scrolling
  // (i.e. with two fingers on touchpad, or with a mouse scrollwheel).
  const {translation, isPanning, centerLayout, startDiagramPan, shiftDiagramByWheelEvent} =
    useDiagramPanningState();
  const translationStyle = `translate3d(${translation.x}px, ${translation.y}px, 0px)`;

  useDocumentCursor('move', isPanning);

  const diagramWrapperRef = useRef<HTMLDivElement>(null);

  useOnMount(() => {
    centerLayout(diagramWrapperRef);
    window.addEventListener(
      'resize',
      throttle(() => {
        centerLayout(diagramWrapperRef);
      }, 20)
    );
  });

  // Memoize so that the list of tables and relationship SVG aren't re-rendered on events like panning or
  // resizing, which would be a big performance hit.
  const relationshipSVG = useMemo(
    () => <WarehouseDiagramRelationshipSVG models={warehouseModels} />,
    [warehouseModels]
  );
  const tableList = useMemo(
    () =>
      Array.from(layout.tableNameToCell).flatMap(([tableName, cell], key) => {
        if (cellIsObscured(cell, warehouseModels)) {
          return [];
        } else {
          return [
            <WarehouseDiagramTable
              schemaName={schemaName}
              tableData={warehouseModels.tables.get(tableName)!}
              columnsExpanded={layout.verticallyExpandedTables.has(tableName)}
              isFocus={tableName === focusTableName}
              cell={cell}
              key={key}
            />,
          ];
        }
      }),
    [
      focusTableName,
      schemaName,
      warehouseModels,
      layout.tableNameToCell,
      layout.verticallyExpandedTables,
      cellIsObscured,
    ]
  );

  return (
    <DiagramPanningContext.Provider value={isPanning}>
      <div
        className={css(styles.wrapper)}
        ref={diagramWrapperRef}
        onWheel={shiftDiagramByWheelEvent}
        onMouseDown={(e) => {
          startDiagramPan(e);
          hideRelationshipTooltip();
        }}
        // Any mouse movement on the diagram when there is a hovered relationship should stop hovering a
        // relationship unless the mouse is currently over the hovered relationship. Therefore, the child
        // WarehouseDiagramRelationship stops propagation on its mousemove events so that this
        // handler is not called when hovering a relationship.
        // We do this instead of onMouseOut on the relationship SVG path because the path is very thin and
        // relatively fast mouse movement will lead to no mouseout event being triggered, and the relationship
        // remaining hovered.
        onMouseMove={() => {
          if (layout.hoveredRelationship) {
            stopHoverRelationship();
          }
        }}
      >
        <div
          className={css(styles.diagramOverlayWrapper)}
          style={{
            transform: translationStyle,
          }}
        >
          {relationshipSVG}
        </div>
        <div
          className={classNames(css(styles.diagramOverlayWrapper, styles.tableOverlayWrapper), 'u-no-select')}
          style={{
            transform: translationStyle,
          }}
        >
          {tableList}
        </div>
        {layout.relationshipTooltip && diagramWrapperRef.current && (
          <WarehouseRelationshipTooltip
            tooltipData={layout.relationshipTooltip}
            diagramWrapperRef={diagramWrapperRef}
          />
        )}
        <WarehouseDiagramLegend />
      </div>
    </DiagramPanningContext.Provider>
  );
}

export default function WarehouseDiagram(props: WarehouseDiagramProps): JSX.Element {
  const focusTableData = props.warehouseModels.tables.get(props.focusTableName)!;
  const diagramLayoutState = useDiagramLayoutState(focusTableData);

  return (
    <DiagramLayoutContext.Provider value={diagramLayoutState}>
      <WarehouseDiagramContents {...props} />
    </DiagramLayoutContext.Provider>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    position: 'relative', // Allow children to be absolutely positioned (particularly the legend)
    width: '100%',
    height: '100%',
    backgroundColor: GRAY_LIGHTER,
    cursor: 'move',
    overflow: 'hidden', // Overflow outside of the diagram wrapper should be hidden.
  },
  diagramOverlayWrapper: {
    width: '100%',
    height: '100%',
    position: 'absolute',
    overflow: 'visible', // Overflow within the diagram wrapper should be displayed.
  },
  tableOverlayWrapper: {
    // Allows pointer events to fall through to the relationship SVG rather than be captured by
    // the blank space between tables.
    pointerEvents: 'none',
  },
});
